import React, { useState } from 'react'
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  Typography, IconButton, Chip
} from '@material-ui/core'
import { ExpandMore, ExpandLess } from '@material-ui/icons'

const fmt = (v) => {
  if (!v || v === 0) return '—'
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const headerStyle = {
  fontWeight: 700,
  fontSize: '0.8rem',
  backgroundColor: 'var(--df-primary, #1f5e3a)',
  color: '#fbf8f1',
  padding: '8px 12px'
}

const pkgHeaderStyle = {
  fontWeight: 700,
  fontSize: '0.85rem',
  backgroundColor: 'var(--df-primary-2, #164a2c)',
  color: '#fbf8f1',
  padding: '8px 12px'
}

const subtotalStyle = {
  fontWeight: 700,
  fontSize: '0.8rem',
  backgroundColor: '#eceff1',
  padding: '6px 12px'
}

const INFORMATIONAL_TYPES = ['header', 'subtotal', 'informational', 'note']

const statusChip = (item, isInformational) => {
  if (isInformational) return <Chip size="small" label="Info" style={{ height: 20, fontSize: 10, backgroundColor: '#eeeeee', color: '#666' }} />
  if (item.notInScope) return <Chip size="small" label="Not In Scope" style={{ height: 20, fontSize: 10, backgroundColor: '#ffebee', color: '#b71c1c' }} />
  if (item.hasAllocation) return <Chip size="small" label="Allocated" style={{ height: 20, fontSize: 10, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
  return <Chip size="small" label="Unallocated" style={{ height: 20, fontSize: 10, backgroundColor: '#fff3e0', color: '#e65100' }} />
}

const BidResponseOutputTable = ({ data }) => {
  const [expanded, setExpanded] = useState({})

  if (!data || !data.packages) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ padding: 20 }}>
        No bid response output data available
      </Typography>
    )
  }

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const grandTotal = data.packages.reduce((sum, pkg) => sum + (pkg.subtotal || 0), 0)

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table size="small" style={{ borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ ...headerStyle, width: 30 }} />
            <TableCell style={{ ...headerStyle, width: 80 }}>Item No.</TableCell>
            <TableCell style={headerStyle}>Description</TableCell>
            <TableCell style={{ ...headerStyle, width: 110 }}>Status</TableCell>
            <TableCell style={{ ...headerStyle, width: 80 }}>Unit</TableCell>
            <TableCell align="right" style={{ ...headerStyle, width: 80 }}>Qty</TableCell>
            <TableCell align="right" style={{ ...headerStyle, width: 120 }}>Unit Price</TableCell>
            <TableCell align="right" style={{ ...headerStyle, width: 120 }}>Total Price</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.packages.map((pkg) => (
            <React.Fragment key={pkg.packageId}>
              {/* Package header */}
              <TableRow>
                <TableCell colSpan={8} style={pkgHeaderStyle}>
                  {pkg.packageName}
                </TableCell>
              </TableRow>

              {/* Item rows */}
              {(pkg.items || []).map((item, idx) => {
                const rowKey = `${pkg.packageId}-${idx}`
                const isInformational = INFORMATIONAL_TYPES.includes((item.itemType || '').toLowerCase())
                const hasAllocation = item.hasAllocation

                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      hover={hasAllocation && !isInformational}
                      style={{
                        cursor: hasAllocation && !isInformational ? 'pointer' : 'default',
                        backgroundColor: !hasAllocation && !isInformational ? '#fafafa' : undefined
                      }}
                      onClick={() => {
                        if (hasAllocation && !isInformational) toggleExpand(rowKey)
                      }}
                    >
                      <TableCell style={{ padding: '4px 8px' }}>
                        {hasAllocation && !isInformational && (
                          <IconButton size="small">
                            {expanded[rowKey] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell
                        style={{
                          fontSize: '0.82rem',
                          fontStyle: isInformational ? 'italic' : 'normal',
                          color: isInformational ? '#888' : undefined
                        }}
                      >
                        {item.itemNo || ''}
                      </TableCell>
                      <TableCell
                        style={{
                          fontSize: '0.85rem',
                          fontStyle: isInformational ? 'italic' : 'normal',
                          color: isInformational ? '#888' : undefined,
                          fontWeight: isInformational ? 400 : 500
                        }}
                      >
                        {item.description || ''}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          fontSize: '0.85rem',
                          color: !hasAllocation && !isInformational ? '#bbb' : undefined
                        }}
                      >
                        {statusChip(item, isInformational)}
                      </TableCell>
                      <TableCell
                        style={{
                          fontSize: '0.8rem',
                          color: '#888',
                          fontStyle: isInformational ? 'italic' : 'normal'
                        }}
                      >
                        {isInformational ? '' : (item.unit || '')}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          fontSize: '0.85rem',
                          color: isInformational ? '#888' : undefined,
                          fontStyle: isInformational ? 'italic' : 'normal'
                        }}
                      >
                        {isInformational ? '' : (item.quantity != null ? item.quantity : '—')}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          fontSize: '0.85rem',
                          color: !hasAllocation && !isInformational ? '#bbb' : undefined
                        }}
                      >
                        {isInformational ? '' : fmt(item.unitSell)}
                      </TableCell>
                      <TableCell
                        align="right"
                        style={{
                          fontSize: '0.85rem',
                          fontWeight: hasAllocation ? 600 : 400,
                          color: !hasAllocation && !isInformational ? '#bbb' : undefined
                        }}
                      >
                        {isInformational ? '' : fmt(item.totalSell)}
                      </TableCell>
                    </TableRow>

                    {/* Expanded allocation detail */}
                    {expanded[rowKey] && hasAllocation && (item.allocations || []).map((alloc, aIdx) => (
                      <TableRow key={aIdx} style={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell />
                        <TableCell />
                        <TableCell
                          colSpan={4}
                          style={{ paddingLeft: 36, fontSize: '0.78rem', color: '#555' }}
                        >
                          <span style={{ fontWeight: 500, color: 'var(--df-ink-2, #3d463a)' }}>{alloc.serviceName || alloc.service || '—'}</span>
                          {alloc.proportion != null && (
                            <span style={{ color: '#999', marginLeft: 8 }}>
                              ({(alloc.proportion * 100).toFixed(1)}%)
                            </span>
                          )}
                        </TableCell>
                        <TableCell align="right" style={{ fontSize: '0.78rem', color: '#555' }}>
                          {alloc.proportion != null && item.quantity > 0 ? fmt((alloc.areaSell || 0) / item.quantity) : '—'}
                        </TableCell>
                        <TableCell align="right" style={{ fontSize: '0.78rem', color: '#555' }}>
                          {fmt(alloc.areaSell)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                )
              })}

              {/* Package subtotal */}
              <TableRow>
                <TableCell colSpan={6} style={subtotalStyle}>
                  {pkg.packageName} Subtotal
                </TableCell>
                <TableCell colSpan={2} align="right" style={subtotalStyle}>
                  {fmt(pkg.subtotal)}
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}

          {/* Grand total */}
          <TableRow>
            <TableCell
              colSpan={6}
              style={{ fontWeight: 700, fontSize: '0.9rem', padding: '12px', borderTop: '2px solid var(--df-primary, #1f5e3a)' }}
            >
              GRAND TOTAL
            </TableCell>
            <TableCell
              colSpan={2}
              align="right"
              style={{ fontWeight: 700, fontSize: '0.9rem', padding: '12px', borderTop: '2px solid var(--df-primary, #1f5e3a)', color: 'var(--df-primary, #1f5e3a)' }}
            >
              {fmt(grandTotal)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}

export default BidResponseOutputTable
